import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, isSupabaseConfigured } from "./supabase";
import {
  cacheRecords,
  clearLocalCache,
  countOutbox,
  putCachedRecord,
  queueMutation,
  readCachedRecords,
  readOutbox,
  removeOutboxItem,
} from "./idb";
import type {
  ActivityItem,
  AppData,
  CalendarEvent,
  EntityType,
  ImportantDate,
  KivItem,
  Matter,
  MdmcRecord,
  MobilePreferences,
  NotificationItem,
  ReminderRule,
  Task,
} from "../types";

const defaultPreferences: MobilePreferences = {
  id: "mobile-preferences",
  theme: "system",
  timeFormat: "24",
  reducedMotion: false,
  reducedTransparency: false,
  quietEnabled: false,
  quietStart: "22:00",
  quietEnd: "07:30",
  notificationsEnabled: true,
  inAppEnabled: true,
  pushEnabled: true,
  soundEnabled: true,
  showMissedOnRestore: true,
  defaultReminderMode: "once",
  defaultLeadMinutes: 15,
  defaultRepeatMinutes: 60,
  defaultDailyTime: "08:30",
};

function deviceId(): string {
  const key = "mdm_device_id";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const value = `mobile-${crypto.randomUUID()}`;
  localStorage.setItem(key, value);
  return value;
}

function recordData<T>(records: MdmcRecord[], type: EntityType): T[] {
  return records
    .filter((record) => record.entity_type === type && !record.deleted_at)
    .map(
      (record) =>
        ({
          ...record.data,
          id: (record.data as { id?: string }).id || record.entity_id,
        }) as T,
    );
}

function deriveData(records: MdmcRecord[]): AppData {
  const preferences = recordData<MobilePreferences>(
    records,
    "mobile_preferences",
  ).find((item) => item.id === "mobile-preferences");
  return {
    matters: recordData<Matter>(records, "matter"),
    tasks: recordData<Task>(records, "task"),
    events: recordData<CalendarEvent>(records, "calendar_event"),
    importantDates: recordData<ImportantDate>(records, "important_date"),
    kivItems: recordData<KivItem>(records, "kiv"),
    reminderRules: recordData<ReminderRule>(records, "reminder_rule"),
    notifications: recordData<NotificationItem>(records, "notification"),
    activity: recordData<ActivityItem>(records, "activity"),
    preferences: { ...defaultPreferences, ...(preferences || {}) },
  };
}

interface SaveOptions {
  activityTitle?: string;
  activityDetail?: string;
  skipActivity?: boolean;
}

interface StoreValue {
  configured: boolean;
  session: Session | null;
  user: User | null;
  loading: boolean;
  syncing: boolean;
  online: boolean;
  lastSync: string | null;
  pendingCount: number;
  realtimeStatus: "connecting" | "live" | "fallback" | "offline";
  error: string | null;
  records: MdmcRecord[];
  data: AppData;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  syncNow: () => Promise<void>;
  saveEntity: <T extends { id: string }>(
    type: EntityType,
    entity: T,
    options?: SaveOptions,
  ) => Promise<void>;
  deleteEntity: (
    type: EntityType,
    entityId: string,
    options?: SaveOptions,
  ) => Promise<void>;
  saveReminderRule: (
    sourceType: ReminderRule["sourceType"],
    sourceId: string,
    rule: Omit<ReminderRule, "id" | "sourceType" | "sourceId">,
  ) => Promise<void>;
  addActivity: (item: Omit<ActivityItem, "id" | "createdAt">) => Promise<void>;
  addNotification: (
    item: Omit<NotificationItem, "id" | "createdAt">,
  ) => Promise<void>;
  findRecord: (type: EntityType, entityId: string) => MdmcRecord | undefined;
  clearDeviceData: () => Promise<void>;
}

const StoreContext = createContext<StoreValue | null>(null);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [records, setRecords] = useState<MdmcRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState<string | null>(
    localStorage.getItem("mdm_last_sync"),
  );
  const [pendingCount, setPendingCount] = useState(0);
  const [realtimeStatus, setRealtimeStatus] = useState<
    "connecting" | "live" | "fallback" | "offline"
  >(navigator.onLine ? "connecting" : "offline");
  const [error, setError] = useState<string | null>(null);
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const replaceRecord = useCallback((record: MdmcRecord) => {
    setRecords((current) => {
      const existing = current.find(
        (item) =>
          item.entity_type === record.entity_type &&
          item.entity_id === record.entity_id,
      );
      if (existing) {
        const existingStamp = Math.max(
          Number(existing.sync_version || 0),
          Date.parse(existing.updated_at || "") || 0,
        );
        const incomingStamp = Math.max(
          Number(record.sync_version || 0),
          Date.parse(record.updated_at || "") || 0,
        );
        if (incomingStamp < existingStamp) return current;
      }
      const next = current.filter(
        (item) =>
          !(
            item.entity_type === record.entity_type &&
            item.entity_id === record.entity_id
          ),
      );
      next.push(record);
      void putCachedRecord(record);
      return next;
    });
  }, []);

  const refreshPendingCount = useCallback(async () => {
    try {
      setPendingCount(await countOutbox());
    } catch {
      setPendingCount(0);
    }
  }, []);

  const flushOutbox = useCallback(async () => {
    const currentSession = sessionRef.current;
    if (!currentSession || !navigator.onLine) return;
    const pending = await readOutbox();
    setPendingCount(pending.length);
    for (const mutation of pending) {
      const { error: upsertError } = await supabase
        .from("mdmc_records")
        .upsert(mutation.record, {
          onConflict: "user_id,entity_type,entity_id",
        });
      if (upsertError) throw upsertError;
      await removeOutboxItem(mutation.id);
    }
    await refreshPendingCount();
  }, [refreshPendingCount]);

  const syncNow = useCallback(async () => {
    const currentSession = sessionRef.current;
    if (!currentSession || !navigator.onLine || syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    setError(null);
    try {
      await flushOutbox();
      const { data, error: fetchError } = await supabase
        .from("mdmc_records")
        .select("*")
        .eq("user_id", currentSession.user.id)
        .order("updated_at", { ascending: true });
      if (fetchError) throw fetchError;
      const next = (data || []) as MdmcRecord[];
      setRecords(next);
      await cacheRecords(next);
      const timestamp = new Date().toISOString();
      setLastSync(timestamp);
      localStorage.setItem("mdm_last_sync", timestamp);
      if (navigator.onLine)
        setRealtimeStatus((status) =>
          status === "live" ? status : "fallback",
        );
    } catch (syncError) {
      setError(
        syncError instanceof Error ? syncError.message : String(syncError),
      );
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [flushOutbox]);

  useEffect(() => {
    let mounted = true;
    void readCachedRecords().then((cached) => {
      if (mounted && cached.length) setRecords(cached);
    });
    void refreshPendingCount();
    if (!isSupabaseConfigured) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }
    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      sessionRef.current = data.session;
      setSession(data.session);
      setLoading(false);
      if (data.session) setTimeout(() => void syncNow(), 0);
    });
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        sessionRef.current = nextSession;
        setSession(nextSession);
        if (nextSession) setTimeout(() => void syncNow(), 0);
        else setRecords([]);
      },
    );
    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [refreshPendingCount, syncNow]);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      setRealtimeStatus("connecting");
      void syncNow();
    };
    const onOffline = () => {
      setOnline(false);
      setRealtimeStatus("offline");
    };
    const refresh = () => {
      if (document.visibilityState === "visible") void syncNow();
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    const timer = window.setInterval(refresh, 20_000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(timer);
    };
  }, [syncNow]);

  useEffect(() => {
    if (!session || !navigator.onLine) return;
    setRealtimeStatus("connecting");
    const channel = supabase
      .channel(`mdmc-mobile-${session.user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "mdmc_records",
          filter: `user_id=eq.${session.user.id}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            const previous = payload.old as Partial<MdmcRecord>;
            setRecords((current) =>
              current.filter(
                (item) =>
                  !(
                    item.entity_type === previous.entity_type &&
                    item.entity_id === previous.entity_id
                  ),
              ),
            );
          } else {
            replaceRecord(payload.new as MdmcRecord);
          }
          const timestamp = new Date().toISOString();
          setLastSync(timestamp);
          localStorage.setItem("mdm_last_sync", timestamp);
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          setRealtimeStatus("live");
          void syncNow();
        } else if (
          status === "CHANNEL_ERROR" ||
          status === "TIMED_OUT" ||
          status === "CLOSED"
        )
          setRealtimeStatus(navigator.onLine ? "fallback" : "offline");
      });
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [replaceRecord, session, syncNow]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) throw signInError;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setRecords([]);
    await clearLocalCache();
  }, []);

  const addActivity = useCallback(
    async (item: Omit<ActivityItem, "id" | "createdAt">) => {
      const id = crypto.randomUUID();
      const createdAt = new Date().toISOString();
      const entity: ActivityItem = {
        ...item,
        id,
        createdAt,
        updatedAt: createdAt,
      };
      const currentSession = sessionRef.current;
      if (!currentSession) return;
      const record: MdmcRecord = {
        user_id: currentSession.user.id,
        entity_type: "activity",
        entity_id: id,
        data: entity,
        updated_at: createdAt,
        deleted_at: null,
        sync_version: Date.now(),
        source_device_id: deviceId(),
      };
      replaceRecord(record);
      await queueMutation(record);
      await refreshPendingCount();
      if (navigator.onLine) void flushOutbox();
    },
    [flushOutbox, refreshPendingCount, replaceRecord],
  );

  const saveEntity = useCallback(
    async <T extends { id: string }>(
      type: EntityType,
      entity: T,
      options: SaveOptions = {},
    ) => {
      const currentSession = sessionRef.current;
      if (!currentSession) throw new Error("Sign in before saving changes.");
      const now = new Date().toISOString();
      const record: MdmcRecord<T> = {
        user_id: currentSession.user.id,
        entity_type: type,
        entity_id: entity.id,
        data: {
          ...entity,
          updatedAt: (entity as { updatedAt?: string }).updatedAt || now,
        },
        updated_at: now,
        deleted_at: null,
        sync_version: Date.now(),
        source_device_id: deviceId(),
      };
      replaceRecord(record as MdmcRecord);
      await queueMutation(record as MdmcRecord);
      await refreshPendingCount();
      if (!options.skipActivity && options.activityTitle) {
        await addActivity({
          title: options.activityTitle,
          detail: options.activityDetail,
          entityType: type,
          entityId: entity.id,
          matterId: (entity as { matterId?: string }).matterId,
          eventType: "mobile-change",
        });
      }
      if (navigator.onLine) void flushOutbox();
    },
    [addActivity, flushOutbox, refreshPendingCount, replaceRecord],
  );

  const deleteEntity = useCallback(
    async (type: EntityType, entityId: string, options: SaveOptions = {}) => {
      const currentSession = sessionRef.current;
      if (!currentSession) throw new Error("Sign in before deleting records.");
      const existing = records.find(
        (record) =>
          record.entity_type === type && record.entity_id === entityId,
      );
      const now = new Date().toISOString();
      const record: MdmcRecord = {
        user_id: currentSession.user.id,
        entity_type: type,
        entity_id: entityId,
        data: existing?.data || { id: entityId },
        updated_at: now,
        deleted_at: now,
        sync_version: Date.now(),
        source_device_id: deviceId(),
      };
      replaceRecord(record);
      await queueMutation(record);
      await refreshPendingCount();
      if (!options.skipActivity)
        await addActivity({
          title: options.activityTitle || "Deleted on iPhone",
          detail: options.activityDetail,
          entityType: type,
          entityId,
          eventType: "mobile-delete",
        });
      if (navigator.onLine) void flushOutbox();
    },
    [addActivity, flushOutbox, records, refreshPendingCount, replaceRecord],
  );

  const saveReminderRule = useCallback(
    async (
      sourceType: ReminderRule["sourceType"],
      sourceId: string,
      rule: Omit<ReminderRule, "id" | "sourceType" | "sourceId">,
    ) => {
      const existingRecord = records.find(
        (record) =>
          record.entity_type === "reminder_rule" &&
          (record.data as unknown as ReminderRule).sourceType === sourceType &&
          (record.data as unknown as ReminderRule).sourceId === sourceId &&
          !record.deleted_at,
      );
      const id =
        (existingRecord?.data as ReminderRule | undefined)?.id ||
        crypto.randomUUID();
      const sourceKey = `${sourceType}:${sourceId}`;
      const entity: ReminderRule = {
        id,
        sourceType,
        sourceId,
        ...rule,
        _sourceKey: sourceKey,
        updatedAt: new Date().toISOString(),
      };
      await saveEntity("reminder_rule", entity, {
        activityTitle: "Reminder settings updated",
        activityDetail: sourceKey,
      });
    },
    [records, saveEntity],
  );

  const addNotification = useCallback(
    async (item: Omit<NotificationItem, "id" | "createdAt">) => {
      const id = crypto.randomUUID();
      const createdAt = new Date().toISOString();
      await saveEntity(
        "notification",
        { ...item, id, createdAt, updatedAt: createdAt },
        { skipActivity: true },
      );
    },
    [saveEntity],
  );

  const findRecord = useCallback(
    (type: EntityType, entityId: string) =>
      records.find(
        (record) =>
          record.entity_type === type && record.entity_id === entityId,
      ),
    [records],
  );

  const clearDeviceData = useCallback(async () => {
    await clearLocalCache();
    setRecords([]);
    setPendingCount(0);
    if (sessionRef.current) await syncNow();
  }, [syncNow]);

  const data = useMemo(() => deriveData(records), [records]);

  const value = useMemo<StoreValue>(
    () => ({
      configured: isSupabaseConfigured,
      session,
      user: session?.user || null,
      loading,
      syncing,
      online,
      lastSync,
      pendingCount,
      realtimeStatus,
      error,
      records,
      data,
      signIn,
      signOut,
      syncNow,
      saveEntity,
      deleteEntity,
      saveReminderRule,
      addActivity,
      addNotification,
      findRecord,
      clearDeviceData,
    }),
    [
      session,
      loading,
      syncing,
      online,
      lastSync,
      pendingCount,
      realtimeStatus,
      error,
      records,
      data,
      signIn,
      signOut,
      syncNow,
      saveEntity,
      deleteEntity,
      saveReminderRule,
      addActivity,
      addNotification,
      findRecord,
      clearDeviceData,
    ],
  );

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) throw new Error("useStore must be used inside StoreProvider");
  return value;
}
